import React, { FC, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Button, Switch, Tooltip } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import DatePickerDropdown from 'app/components/datepicker-dropdown';
import DatePickerHeaderMonthNav from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderMonthNav';
import DatePickerHeaderDefault from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderDefault';
import DatePickerRangeInputs from 'app/components/datepicker-dropdown/DatePickerRangeInputs';
import DatePickerFooter from 'app/components/datepicker-dropdown/DatePickerFooter';
import DatePickerPresets from 'app/components/datepicker-dropdown/DatePickerPresets';
import {
  getDatesAutoSelectPeriodWithConstrains,
  getDatesFromPreset,
  getDatesWithConstraints,
  isDateWithinRange,
} from 'app/components/datepicker-dropdown/utils';
import useSetState from 'utils/hooks/useSetState';
import { getRangePlaceholderLabel } from 'app/components/datepicker-dropdown/utils/placeholder';
import { DatePreset } from 'app/components/datepicker-dropdown/enums';
import { DATE_PRESET_LABELS } from 'app/components/datepicker-dropdown/constants/presets';
import { getAnalyticsMinSelectableDate } from 'app/modules/analytics/utils/analyticsDateLimit';
import type { AnalyticsDateRange } from 'app/modules/analytics/utils/analyticsDateLimit';
import {
  ANALYTICS_DATE_PRESETS,
  classifyAnalyticsRange,
} from 'app/modules/analytics/utils/analyticsDateRangeShape';
import type { AnalyticsDatePreset } from 'app/modules/analytics/utils/analyticsDateRangeShape';

const RANGE_MONTHS_SHOWN = 2;

/** Shared width for the Presets and Month tabs so switching them doesn't resize the popover. */
const COMPACT_VIEW_WIDTH = 'tw-w-[340px]';

type MainSelectorView = 'presets' | 'month';

interface Props {
  datePreset: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: (
    dates: { startDate: Dayjs | null; endDate: Dayjs | null },
    preset?: string,
  ) => void;
  contentClassname?: string;
  dataCy?: string;
  /**
   * Set only on the comparison picker: the period being compared against. It decides
   * whether months may be picked, and fixes the width of a custom comparison window.
   */
  primaryPeriod?: AnalyticsDateRange;
  comparisonPresets?: { value: string; label: string; isDisabled?: boolean }[];
  onComparisonPresetSelect?: (value: string) => void;
}

const AnalyticsDatePicker: FC<Props> = ({
  datePreset,
  startDate,
  endDate,
  onChange,
  contentClassname,
  dataCy,
  primaryPeriod,
  comparisonPresets,
  onComparisonPresetSelect,
}) => {
  const isComparisonMode = primaryPeriod != null;
  const comparisonPresetLabelByValue = useMemo(
    () =>
      new Map(
        (comparisonPresets ?? []).map(({ value, label }) => [value, label]),
      ),
    [comparisonPresets],
  );

  const MIN_DATE = useMemo(() => getAnalyticsMinSelectableDate(), []);
  const MAX_DATE = useMemo(() => dayjs().endOf('day'), []);

  /**
   * A custom comparison window is a fixed-width slider, not a free range: the user
   * picks only where it starts, and it always spans exactly as many days as the period
   * being compared against. That is why Custom survives on this picker while the main
   * one has none — it cannot produce a window wider than one already being served.
   */
  const comparisonPeriodDays = useMemo(
    () =>
      primaryPeriod
        ? primaryPeriod.endDate.diff(primaryPeriod.startDate, 'day') + 1
        : null,
    [primaryPeriod],
  );

  const rangeShape = useMemo(
    () => classifyAnalyticsRange({ startDate, endDate }, datePreset),
    [startDate, endDate, datePreset],
  );

  const [preset, setPreset] = useState<string | undefined>(
    datePreset ?? undefined,
  );
  const [mainView, setMainView] = useState<MainSelectorView>('presets');
  const [isOpen, setIsOpen] = useState(false);
  const [startDateTemp, setStartDateTemp] = useState<Dayjs | null>(null);
  const [endDateTemp, setEndDateTemp] = useState<Dayjs | null>(null);
  const [error, setError] = useSetState<{
    startDate: boolean;
    endDate: boolean;
  }>({ startDate: true, endDate: true });

  const { t } = useTranslation();

  const presetLabel =
    preset == null || isComparisonMode
      ? undefined
      : comparisonPresetLabelByValue.get(preset) ??
        (ANALYTICS_DATE_PRESETS.includes(preset as AnalyticsDatePreset)
          ? DATE_PRESET_LABELS[preset as DatePreset]
          : undefined);

  const isRangeViewActive = isComparisonMode;
  const isMonthViewActive = !isComparisonMode && mainView === 'month';
  const isPresetViewActive = !isComparisonMode && mainView === 'presets';

  const rangeViewAnchorDate = useMemo(
    () => (isRangeViewActive ? startDate.startOf('month').toDate() : undefined),
    [isRangeViewActive, startDate],
  );

  useEffect(() => {
    setPreset(datePreset ?? undefined);
  }, [datePreset]);

  useEffect(() => {
    setMainView(rangeShape.kind === 'month' ? 'month' : 'presets');
  }, [rangeShape]);

  useEffect(() => {
    if (!isOpen) {
      setStartDateTemp(startDate);
      setEndDateTemp(endDate);
      setPreset(datePreset ?? undefined);
    }
  }, [isOpen, startDate, endDate, datePreset]);

  useEffect(() => {
    setError({
      startDate: isDateWithinRange(startDate, MIN_DATE, MAX_DATE),
      endDate: isDateWithinRange(endDate, MIN_DATE, MAX_DATE),
    });
  }, [startDate, endDate, setError, MAX_DATE, MIN_DATE]);

  const handlePresetSelect = (nextPreset?: string) => {
    setPreset(nextPreset);

    if (!nextPreset) {
      onChange({ startDate, endDate }, undefined);
      return;
    }

    onChange(
      getDatesWithConstraints(getDatesFromPreset(nextPreset), {
        maxEndDate: MAX_DATE,
      }),
      nextPreset,
    );
    setMainView('presets');
    setStartDateTemp(null);
    setEndDateTemp(null);
    setIsOpen(false);
  };

  const handleMonthSelect = (date: Date) => {
    const monthStart = dayjs(date).startOf('month');

    setPreset(undefined);
    setStartDateTemp(null);
    setEndDateTemp(null);
    onChange(
      getDatesWithConstraints(
        { startDate: monthStart, endDate: monthStart.endOf('month') },
        { maxEndDate: MAX_DATE },
      ),
      undefined,
    );
    setIsOpen(false);
  };

  const onRangeSelect = (dates: [Date | null, Date | null]) => {
    const [start] = dates;

    setPreset(undefined);

    if (!start || comparisonPeriodDays == null) {
      setStartDateTemp(null);
      setEndDateTemp(null);
      return;
    }

    const autoSelected = getDatesAutoSelectPeriodWithConstrains(
      { startDate: dayjs(start), endDate: dayjs(start) },
      comparisonPeriodDays - 1,
      { maxEndDate: MAX_DATE },
    );

    setStartDateTemp(autoSelected.startDate ?? dayjs(start));
    setEndDateTemp(autoSelected.endDate ?? null);
  };

  const handleStartDateInputChange = (date: Dayjs | null) => {
    setPreset(undefined);
    setStartDateTemp(date);
  };

  const handleEndDateInputChange = (date: Dayjs | null) => {
    setPreset(undefined);
    setEndDateTemp(date);
  };

  const onDateRangeSubmit = () => {
    onChange(
      { startDate: startDateTemp ?? null, endDate: endDateTemp ?? null },
      undefined,
    );
    setIsOpen(false);
  };

  const onCancelCustomPeriod = () => {
    setStartDateTemp(startDate);
    setEndDateTemp(endDate);
    setIsOpen(false);
  };

  const handleMainViewChange = (nextView: MainSelectorView) => {
    if (nextView === mainView) {
      return;
    }

    setMainView(nextView);
  };

  return (
    <DatePickerDropdown
      contentClassname={clsx(
        'tw-text-md',
        { [COMPACT_VIEW_WIDTH]: !isRangeViewActive },
        contentClassname,
      )}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      modal={false}
      dataCy={dataCy ? { placeholder: dataCy } : undefined}
      label={
        presetLabel ??
        getRangePlaceholderLabel({
          startDateTemp: startDateTemp ?? startDate,
          endDateTemp: endDateTemp ?? endDate,
        })
      }
      selected={isMonthViewActive ? startDate.toDate() : null}
      startDate={isRangeViewActive ? startDateTemp?.toDate() ?? null : null}
      endDate={isRangeViewActive ? endDateTemp?.toDate() ?? null : null}
      selectsRange={isRangeViewActive}
      showMonthYearPicker={isMonthViewActive}
      dateFormat={isMonthViewActive ? 'MM/yyyy' : undefined}
      hideDefaultDatePicker={isPresetViewActive}
      maxDate={MAX_DATE.toDate()}
      minDate={MIN_DATE.toDate()}
      openToDate={rangeViewAnchorDate}
      monthsShown={isRangeViewActive ? RANGE_MONTHS_SHOWN : 1}
      onChange={(val) => {
        if (isRangeViewActive) {
          onRangeSelect(val as [Date | null, Date | null]);
          return;
        }

        if (isMonthViewActive && val) {
          handleMonthSelect(val as Date);
        }
      }}
      topContent={
        <>
          {isComparisonMode && comparisonPresets && (
            <div
              className="tw-flex tw-gap-2 tw-border-b tw-border-grayscale-20 tw-p-3"
              data-cy="analytics-comparison-presets"
            >
              {comparisonPresets.map(({ value, label, isDisabled }) => (
                <Tooltip
                  key={value}
                  content={
                    isDisabled
                      ? t('analyticsComparisonPresetUnavailable')
                      : undefined
                  }
                >
                  <div className="tw-flex-1">
                    <Button
                      size="small"
                      variant={preset === value ? 'primary' : 'ghost'}
                      disabled={isDisabled}
                      onClick={() => {
                        setPreset(value);
                        onComparisonPresetSelect?.(value);
                        setIsOpen(false);
                      }}
                      className={clsx(
                        'tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center !tw-p-2',
                        { 'tw-font-normal': preset !== value },
                      )}
                    >
                      {label}
                    </Button>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
          {!isComparisonMode && (
            <div className="tw-border-b tw-border-grayscale-20 tw-p-3">
              <Switch.List>
                <Switch.Tab
                  active={isPresetViewActive}
                  onClick={() => handleMainViewChange('presets')}
                  data-cy="analytics_date-picker_tab-presets"
                  data-name="analytics_date-picker_tab-presets"
                >
                  {t('presets')}
                </Switch.Tab>
                <Switch.Tab
                  active={isMonthViewActive}
                  onClick={() => handleMainViewChange('month')}
                  data-cy="analytics_date-picker_tab-month"
                  data-name="analytics_date-picker_tab-month"
                >
                  {t('month')}
                </Switch.Tab>
              </Switch.List>
            </div>
          )}
          {isRangeViewActive && (
            <div className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-border-b tw-border-grayscale-20 tw-p-3">
              <DatePickerRangeInputs
                error={error}
                startDate={startDateTemp ?? startDate}
                endDate={endDateTemp ?? endDate}
                onStartDateChange={handleStartDateInputChange}
                onEndDateChange={handleEndDateInputChange}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                dataCy={{
                  startDate: 'analytics_date-picker_start-date-input',
                  endDate: 'analytics_date-picker_end-date-input',
                }}
              />
            </div>
          )}
        </>
      }
      renderCustomHeader={(headerProps) => {
        if (isRangeViewActive) {
          return (
            <DatePickerHeaderMonthNav
              {...headerProps}
              maxDate={MAX_DATE}
              minDate={MIN_DATE}
              monthsShown={RANGE_MONTHS_SHOWN}
            />
          );
        }

        if (isMonthViewActive) {
          return (
            <DatePickerHeaderDefault
              {...headerProps}
              maxDate={MAX_DATE}
              minDate={MIN_DATE}
            />
          );
        }

        return false;
      }}
    >
      {isPresetViewActive && (
        <DatePickerPresets
          presets={[...ANALYTICS_DATE_PRESETS]}
          onPresetSelect={handlePresetSelect}
          selectedPreset={preset}
          widthClassName="tw-w-full"
        />
      )}
      {isRangeViewActive && (
        <DatePickerFooter
          isCancelDisabled={
            startDateTemp === startDate && endDateTemp === endDate
          }
          onDateRangeSubmit={onDateRangeSubmit}
          onRangeSelectCancel={onCancelCustomPeriod}
        />
      )}
    </DatePickerDropdown>
  );
};

export default AnalyticsDatePicker;
